import { Component, Inject, OnInit, ViewChild, TemplateRef, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MemberService } from '../../services/member/member.service';
import { User } from '../../interfaces/user.interface';
import { AppCenterAnalytics, AppCenterAnalyticsAPIMsgs } from '../../services/app-insights/app-insights';
import { AppInsightsService } from '../../services/app-insights/app-insights.service';
import { emailPattern } from '../main.constants';
import { MultilingualService } from 'src/app/core/services/multilingual/multilingual.service';
import { UserService } from '../../services/user/user.service';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AuthService } from '../../core/services/auth/auth.service';
import { TokenService } from '../../core/services/token/token.service';
import { Subscription } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ContactInfo } from 'src/app/interfaces/contact-info.interface';
import { AppConstants } from '../../app.constants';

@Component({
  selector: 'app-update-profile',
  templateUrl: './update-profile.component.html',
  styleUrls: ['./update-profile.component.scss']
})
export class UpdateProfileComponent implements OnInit, OnDestroy {
  userForm: UntypedFormGroup;
  isError = false;
  isLoading = false;
  private readonly subscription = new Subscription();
  @ViewChild('success', { read: TemplateRef }) successTemplate!: TemplateRef<any>;
  @ViewChild('error', { read: TemplateRef }) errorTemplate!: TemplateRef<any>;
  emailUpdate = false;
  successMsg!: string;
  errorMsg!: string;
  otpVerified = true;
  resendOtp = false;
  otpValue = '';
  showError = false;
  mobileNumber = '';
  loaded = false;
  isLoggedInMemberTermed = false;
  userEligibilityStatus: string | undefined;

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly router: Router,
    private readonly memberService: MemberService,
    private readonly authService: AuthService,
    private readonly appInsightsService: AppInsightsService,
    private readonly userService: UserService,
    private readonly tokenService: TokenService,
    public readonly multilingualService: MultilingualService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<UpdateProfileComponent>,
    private readonly snackBar: MatSnackBar,
  ) {
    this.isLoggedInMemberTermed = userService.isMemberTermed;
    if (this.memberService.user) {
      const userRxGrp = this.memberService.user.rxGrp === AppConstants.EXTERNAL_CLIENT_IDS_MAPPING.CLIENT_ID ? AppConstants.EXTERNAL_CLIENT_IDS_MAPPING.EXTERNAL_CLIENT_ID : this.memberService.user.rxGrp;
      this.userForm = this.fb.group({
        email: [{ value: this.memberService.user.email, disabled: this.isLoggedInMemberTermed }, [Validators.required, Validators.pattern(emailPattern)]],
        firstName: [this.memberService.user.firstName, [Validators.required]],
        lastName: [this.memberService.user.lastName, [Validators.required]],
        contact: [{ value: this.mobileNumber, disabled: this.isLoggedInMemberTermed }, [Validators.required, Validators.minLength(10), Validators.maxLength(10)]],
        dob: [{ value: this.memberService.user.dateOfBirth, disabled: true }, [Validators.required]],
        cardId: [{ value: this.memberService.user.cardId, disabled: true }, [Validators.required]],
        rxGrp: [{ value: userRxGrp, disabled: true }, [Validators.required]]
      });
      this.userForm.controls.email.markAsTouched();
      this.userForm.controls.contact.markAsTouched();
    }
    else {
      this.showError = true;
      this.userForm = this.fb.group({})
    }
  }

  ngOnInit(): void {
    this.data ? this.appInsightsService.trackPageView(AppCenterAnalytics.ACCOUNT_PROFILE_VIEWED)
      : this.appInsightsService.trackPageView(AppCenterAnalytics.UPDATE_PROFILE_VIEWED);
    //this.appInsightsService.trackPageView(AppCenterAnalytics.UPDATE_PROFILE_VIEWED);
    this.getContactInfo();
    this.userEligibilityStatus = this.userService.eligibilityVerificationResponse?.eligibilityStatus;
  }

  getContactInfo(): void {
    this.subscription.add(this.memberService.getContactInfo().subscribe((data: ContactInfo) => {
      if (data && data.mobileNumber) {
        this.mobileNumber = data.mobileNumber;
        this.userForm.patchValue({
          contact: data.mobileNumber,
        });
      }
      this.loaded = true;
    }, () => {
      this.loaded = true;
      this.errorMsg = this.multilingualService.langMap?.LABEL_GET_MOBILE_NUMBER_FAILED;
      this.snackBar.openFromTemplate(this.errorTemplate, {
        duration: 4000,
        verticalPosition: 'top',
        horizontalPosition: 'center'
      });
    }));
  }
  getUserToUpdate(): User {
    return {
      cardId: this.memberService.user.cardId,
      clientId: this.memberService.user.clientId,
      contactNumber: this.userForm.get('contact')?.value || '',
      dateOfBirth: this.memberService.user.dateOfBirth,
      email: this.userForm.get('email')?.value || '',
      firstName: this.userForm.get('firstName')?.value || '',
      lastName: this.userForm.get('lastName')?.value || '',
      userName: this.memberService.user.userName,
      personCode: this.userService.eligibilityVerificationResponse?.personCode,
      objectId: this.userService.getObjectId()
    };
  }

  getSsoUserToUpdate(): User {
    return {
      cardId: this.userService.eligibilityVerificationResponse?.cardId,
      clientId: this.userService.eligibilityVerificationResponse?.clientId,
      contactNumber: this.userForm.get('contact')?.value || '',
      dateOfBirth: this.userService.member?.dateOfBirth.toString(),
      email: this.userForm.get('email')?.value || '',
      firstName: this.userForm.get('firstName')?.value || '',
      lastName: this.userForm.get('lastName')?.value || '',
      personCode: this.userService.eligibilityVerificationResponse?.personCode,
      userName: this.memberService.user.userName,
      objectId: ''
    };
  }

  update(): void {
    this.isError = true;
    this.isLoading = true;
    let api;
    if (this.authService.isAuthorizedByAzureB2c) {
      api = this.memberService.updateMemberDetails(this.getUserToUpdate());
    } else {
      api = this.memberService.UpdateSsoMemberDetail(this.getSsoUserToUpdate());
    }
    let responseText = AppCenterAnalyticsAPIMsgs.COMMON_INITIAL_MSG;
    api.subscribe((res) => {
      if (this.authService.isAuthorizedBySSO) {
        this.tokenService.setSSOEmail(this.userForm.get('email')?.value || '');
      }
      responseText = AppCenterAnalyticsAPIMsgs.SUCCESS_UPDATE_PROFILE;
      this.appInsightsService.trackPageView(`${AppCenterAnalytics.ACCOUNT_PROFILE_VIEWED} ${AppCenterAnalytics.COMMON_API_CALL}`, { formData: this.userForm.value, Response: responseText });
      this.isLoading = false;
      this.dialogRef.close({ dialogStatus: 0 });
      this.router.navigate(['/main']);
    }, (error) => {
      this.isError = true;
      this.isLoading = false;
      this.errorMsg = error;
      responseText = error ? error : AppCenterAnalyticsAPIMsgs.ERROR_UPDATE_PROFILE;
      this.snackBar.openFromTemplate(this.errorTemplate, {
        duration: 4000,
        verticalPosition: 'top',
        horizontalPosition: 'center'
      });
      this.appInsightsService.trackPageView(`${AppCenterAnalytics.ACCOUNT_PROFILE_VIEWED} ${AppCenterAnalytics.COMMON_API_CALL}`, { formData: this.userForm.value, Response: responseText });
      // this.dialogRef.close({ msg: errorMsg, dialogStatus: 1 });
    });
  }

  sendVerificationCode(value: boolean): void {
    this.isLoading = true;
    const username = this.memberService.user.userName;
    const email = this.userForm.get('email')?.value;
    this.subscription.add(this.memberService.sendOTP(email, username).subscribe((res: string) => {
      this.successMsg = res;
      this.snackBar.openFromTemplate(this.successTemplate, {
        duration: 4000,
        verticalPosition: 'top',
        horizontalPosition: 'center'
      });
      this.otpValue = value ? '' : this.otpValue;
      this.resendOtp = true;
      this.otpVerified = false;
      this.isLoading = false;
    }, (err) => {
      this.errorMsg = err;
      this.isLoading = false;
      this.snackBar.openFromTemplate(this.errorTemplate, {
        duration: 4000,
        verticalPosition: 'top',
        horizontalPosition: 'center'
      });
    }));
  }

  verifyCode(): void {
    this.isLoading = true;
    this.subscription.add(this.memberService.verifyOTP(Number(this.otpValue), this.memberService.user.userName).subscribe((res: string) => {
      this.successMsg = res;
      this.snackBar.openFromTemplate(this.successTemplate, {
        duration: 4000,
        verticalPosition: 'top',
        horizontalPosition: 'center'
      });
      this.otpVerified = true;
      this.emailUpdate = false;
      this.isLoading = false;
    }, (err) => {
      this.otpVerified = false;
      this.errorMsg = err;
      this.isLoading = false;
      this.snackBar.openFromTemplate(this.errorTemplate, {
        duration: 4000,
        verticalPosition: 'top',
        horizontalPosition: 'center'
      });
    }));
  }

  checkEmailUpdateEvent(event: any): void {
    this.emailUpdate = false;
    this.otpVerified = true;
    if (this.userForm.controls['email'].value !== this.memberService.user.email) {
      this.emailUpdate = true;
      this.otpVerified = false;
      this.resendOtp = false;
    }
  }

  checkOtpLength(): boolean {
    return ((this.otpValue.length > 6) || (this.otpValue.length < 6)) ? true : false;
  }
  checkIfSpace(number: any): boolean {
    const onlySpace = number.which ? number.which : number.keyCode;
    return (onlySpace < 48 || onlySpace > 57) ? false : true;
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}
